import { useCallback, useEffect, useState } from "react";
import "./Resortreviews.css";


const API_BASE =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_API_URL) ||
  "https://bmtvendorvendorfinal.onrender.com";

const LIMIT = 10;

const STATUS_TABS = [
  ["", "All"],
  ["PENDING", "Pending"],
  ["APPROVED", "Approved"],
  ["REJECTED", "Rejected"],
  ["HIDDEN", "Hidden"],
];

const STATUS_LABEL = Object.fromEntries(STATUS_TABS.filter(([k]) => k));

const STATUS_NOTE = {
  PENDING: "Awaiting approval. Guests cannot see this review yet.",
  REJECTED: "This review was rejected and is not shown to guests.",
  HIDDEN: "This review is hidden from guests.",
};

const SUB_RATINGS = [
  ["cleanliness", "Cleanliness"],
  ["service", "Service"],
  ["location", "Location"],
  ["valueForMoney", "Value for money"],
  ["roomQuality", "Room quality"],
  ["food", "Food"],
];

const REPLY_MAX = 2000;

/* ==========================================================
   HELPERS
========================================================== */

async function request(path, { method = "GET", body, params } = {}) {
  const token = localStorage.getItem("vendorToken");
  const url = new URL(`${API_BASE}${path}`);

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });

  let response;

  try {
    response = await fetch(url.toString(), {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    throw new Error("Cannot reach the server. Check your connection.");
  }

  let data = {};

  try {
    data = await response.json();
  } catch (error) {
    data = {};
  }

  if (!response.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${response.status}).`);
  }

  return data;
}

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "-";

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

/* ==========================================================
   SMALL PIECES
========================================================== */

function Stars({ value, size = 16 }) {
  const rounded = Math.round(Number(value) || 0);

  return (
    <span className="rv-stars" style={{ fontSize: size }} aria-label={`${rounded} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= rounded ? "on" : "off"}>
          ★
        </span>
      ))}
    </span>
  );
}

function SummaryPanel({ summary }) {
  const rows = [
    [5, summary.fiveStar],
    [4, summary.fourStar],
    [3, summary.threeStar],
    [2, summary.twoStar],
    [1, summary.oneStar],
  ];

  return (
    <section className="rv-summary">
      <div className="rv-score">
        <strong>{summary.totalReviews ? summary.averageRating.toFixed(1) : "-"}</strong>
        <Stars value={summary.averageRating} size={20} />
        <span>
          {summary.totalReviews} approved review{summary.totalReviews === 1 ? "" : "s"}
        </span>
      </div>

      <div className="rv-bars">
        {rows.map(([star, count]) => (
          <div className="rv-bar-row" key={star}>
            <span>{star} star</span>
            <div className="rv-bar">
              <div
                className="rv-bar-fill"
                style={{ width: `${summary.totalReviews ? (count / summary.totalReviews) * 100 : 0}%` }}
              />
            </div>
            <em>{count}</em>
          </div>
        ))}
      </div>
    </section>
  );
}

function ReviewCard({ review, onReplied, notify }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const reply = review.vendorReply && review.vendorReply.message ? review.vendorReply : null;
  const canReply = review.isActive && review.status !== "HIDDEN";

  const subRatings = SUB_RATINGS.filter(([key]) => review.ratings && review.ratings[key]);

  const openReply = () => {
    setText(reply ? reply.message : "");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();

    if (!text.trim()) {
      notify("error", "Write a reply before saving.");
      return;
    }

    setSaving(true);

    try {
      const data = await request(`/api/resort-reviews/${review._id}/reply`, {
        method: "PUT",
        body: { message: text.trim() },
      });

      onReplied(review._id, data.review.vendorReply);
      notify("success", "Reply saved.");
      setOpen(false);
    } catch (err) {
      notify("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rv-card">
      <header className="rv-card-head">
        <div>
          <div className="rv-rating-line">
            <Stars value={review.rating} />
            <strong>{review.rating}.0</strong>
            <span className={`rv-pill rv-s-${String(review.status).toLowerCase()}`}>
              {STATUS_LABEL[review.status] || review.status}
            </span>
            {review.isVerifiedStay && <span className="rv-verified">Verified stay</span>}
          </div>
          {review.title && <h3>{review.title}</h3>}
        </div>
        <time className="rv-muted">{fmtDateTime(review.createdAt)}</time>
      </header>

      <p className="rv-comment">{review.comment}</p>

      {(review.pros.length > 0 || review.cons.length > 0) && (
        <div className="rv-proscons">
          {review.pros.length > 0 && (
            <div>
              <h4>Liked</h4>
              <ul>
                {review.pros.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {review.cons.length > 0 && (
            <div>
              <h4>Could be better</h4>
              <ul>
                {review.cons.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {subRatings.length > 0 && (
        <div className="rv-chips">
          {subRatings.map(([key, label]) => (
            <span className="rv-chip" key={key}>
              {label} <b>{review.ratings[key]}/5</b>
            </span>
          ))}
        </div>
      )}

      {review.images.length > 0 && (
        <div className="rv-images">
          {review.images.map((image, i) => (
            <a key={i} href={image.url} target="_blank" rel="noopener noreferrer">
              <img src={image.url} alt={`Guest upload ${i + 1}`} loading="lazy" />
            </a>
          ))}
        </div>
      )}

      <footer className="rv-card-foot">
        <div className="rv-meta">
          <strong>{review.guest.name}</strong>
          <span>
            {review.resort ? review.resort.name : "Resort"}
            {review.booking
              ? ` · ${review.booking.bookingId} · ${fmtDate(review.booking.checkIn)} to ${fmtDate(
                  review.booking.checkOut
                )}`
              : ""}
          </span>
        </div>
      </footer>

      {STATUS_NOTE[review.status] && <p className="rv-note">{STATUS_NOTE[review.status]}</p>}

      {reply && !open && (
        <div className="rv-reply">
          <div className="rv-reply-head">
            <strong>Your reply</strong>
            <span className="rv-muted">{fmtDateTime(reply.repliedAt)}</span>
          </div>
          <p>{reply.message}</p>
          {canReply && (
            <button className="rv-link" onClick={openReply}>
              Edit reply
            </button>
          )}
        </div>
      )}

      {open && (
        <form className="rv-reply-form" onSubmit={save}>
          <label htmlFor={`reply-${review._id}`}>Reply to {review.guest.name}</label>
          <textarea
            id={`reply-${review._id}`}
            rows="4"
            maxLength={REPLY_MAX}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Thank the guest and address anything they raised."
            autoFocus
          />
          <div className="rv-reply-actions">
            <span className="rv-muted">
              {text.length}/{REPLY_MAX}
            </span>
            <div>
              <button type="button" className="rv-btn ghost" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="rv-btn primary" disabled={saving || !text.trim()}>
                {saving ? "Saving reply..." : "Save reply"}
              </button>
            </div>
          </div>
        </form>
      )}

      {!reply && !open && canReply && (
        <button className="rv-btn ghost small" onClick={openReply}>
          Reply to review
        </button>
      )}
    </article>
  );
}

/* ==========================================================
   MAIN PAGE
========================================================== */

export default function ResortReviews() {
  const [resorts, setResorts] = useState([]);
  const [ready, setReady] = useState(false);
  const [resortId, setResortId] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const notify = useCallback((type, text) => setToast({ type, text, id: Date.now() }), []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  /* ---------- resorts (uses the booking options endpoint) ---------- */
  useEffect(() => {
    let cancelled = false;

    request("/api/resort-bookings/options")
      .then((data) => {
        if (cancelled) return;
        const list = data.resorts || [];
        setResorts(list);
        if (list.length) setResortId(list[0]._id);
      })
      .catch((e) => {
        if (!cancelled) notify("error", e.message);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [notify]);

  /* ---------- review list ---------- */
  const loadReviews = useCallback(async () => {
    if (!ready) return;

    setLoading(true);

    try {
      const data = await request("/api/resort-reviews", {
        params: { resortId, status, page, limit: LIMIT },
      });

      setReviews(data.reviews || []);
      setTotal(data.total || 0);
      setTotalPages(Math.max(1, data.totalPages || 1));
    } catch (e) {
      notify("error", e.message);
    } finally {
      setLoading(false);
    }
  }, [ready, resortId, status, page, notify]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  /* ---------- summary (needs a single resort) ---------- */
  useEffect(() => {
    if (!ready) return undefined;

    if (!resortId) {
      setSummary(null);
      return undefined;
    }

    let cancelled = false;

    request(`/api/resort-reviews/summary/${resortId}`)
      .then((data) => {
        if (!cancelled) setSummary(data.summary);
      })
      .catch((e) => {
        if (!cancelled) {
          setSummary(null);
          notify("error", e.message);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ready, resortId, notify]);

  const onReplied = (id, vendorReply) =>
    setReviews((prev) => prev.map((r) => (r._id === id ? { ...r, vendorReply } : r)));

  const changeResort = (value) => {
    setResortId(value);
    setPage(1);
  };

  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };

  return (
    <div className="rv-page">
      <header className="rv-header">
        <div>
          <h1>Ratings and reviews</h1>
          <p>See what guests say about their stay and reply to their feedback.</p>
        </div>
        <select
          className="rv-select"
          value={resortId}
          onChange={(e) => changeResort(e.target.value)}
          aria-label="Filter by resort"
        >
          <option value="">All resorts</option>
          {resorts.map((r) => (
            <option key={r._id} value={r._id}>
              {r.name}
            </option>
          ))}
        </select>
      </header>

      {resortId && summary && <SummaryPanel summary={summary} />}

      {!resortId && (
        <p className="rv-hint">Select a resort to see its rating summary. Only approved reviews count towards it.</p>
      )}

      <nav className="rv-tabs" aria-label="Review status">
        {STATUS_TABS.map(([value, label]) => (
          <button
            key={value || "all"}
            className={`rv-tab ${status === value ? "active" : ""}`}
            onClick={() => changeStatus(value)}
          >
            {label}
          </button>
        ))}
      </nav>

      {loading && <p className="rv-empty">Loading reviews...</p>}

      {!loading && reviews.length === 0 && (
        <p className="rv-empty">
          {status
            ? `No ${STATUS_LABEL[status].toLowerCase()} reviews yet.`
            : "No reviews yet. Guests can review a stay after check-out."}
        </p>
      )}

      {!loading && reviews.length > 0 && (
        <div className="rv-list">
          {reviews.map((review) => (
            <ReviewCard key={review._id} review={review} onReplied={onReplied} notify={notify} />
          ))}
        </div>
      )}

      <footer className="rv-pager">
        <span>
          {total} review{total === 1 ? "" : "s"}
        </span>
        <div>
          <button className="rv-btn ghost small" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            className="rv-btn ghost small"
            disabled={page >= totalPages || loading}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </footer>

      {toast && (
        <div className={`rv-toast ${toast.type}`} role="status" key={toast.id}>
          {toast.text}
        </div>
      )}
    </div>
  );
}